import { GpmPayBadRequestError } from '../../core/errors.js';
import type {
  WebhookDeliveryStatus,
  WebhookDriver,
} from '../../types/enums.js';
import type { WebhookHistory, WebhookSetting } from '../../types/webhook.js';
import { enumArg, intArg, requireArg } from '../args.js';
import { clientFromFlags, type GlobalFlags } from '../context.js';
import { printJson } from '../redact.js';
import { EXIT, fields, out, relativeTime, style, symbols } from '../ui.js';

const DRIVERS: readonly WebhookDriver[] = [
  'HTTP',
  'TELEGRAM',
  'WORDPRESS',
  'GOOGLE_SHEETS',
];

const DELIVERY_STATUSES: readonly WebhookDeliveryStatus[] = [
  'PENDING',
  'DELIVERED',
  'RETRYING',
  'FAILED',
];

export interface WebhookAdminFlags extends GlobalFlags {
  limit?: string | undefined;
  status?: string | undefined;
  driver?: string | undefined;
  setting?: string | undefined;
}

/**
 * How the endpoint is authenticated, in the same words the docs use.
 *
 * The API returns the *encrypted* secret column, which is useless as a
 * credential but is a reliable presence flag.
 */
function authLabel(setting: WebhookSetting): string {
  switch (setting.authorizationType) {
    case 'HMAC':
      return setting.authorizationSecret == null
        ? style.yellow('HMAC (no secret — deliveries are unsigned)')
        : `HMAC (secret set, header ${setting.authorizationHeaderName ?? 'X-GPMPay-Signature'})`;
    case 'API_KEY':
      return setting.authorizationSecret == null
        ? style.yellow('API_KEY (no secret — no header is sent)')
        : `API_KEY (header ${setting.authorizationHeaderName ?? 'Authorization'})`;
    case 'NONE':
      return style.yellow('NONE (no auth header at all)');
  }
}

function scopeLabel(setting: WebhookSetting): string {
  if (setting.scope === 'ALL') return 'scope ALL';
  const count = setting.bankAccounts?.length ?? 0;
  return `scope SPECIFIC (${String(count)} account${count === 1 ? '' : 's'})`;
}

export async function webhookSettings(
  flags: WebhookAdminFlags,
): Promise<number> {
  const client = clientFromFlags(flags);
  const driver = enumArg(flags.driver, 'driver', DRIVERS);
  const limit = intArg(flags.limit, 'limit');

  const page = await client.webhookSettings.list({
    ...(driver === undefined ? {} : { driver }),
    ...(limit === undefined ? {} : { limit }),
  });

  if (flags.json === true) {
    printJson(page);
    return EXIT.OK;
  }

  if (page.data.length === 0) {
    out(style.dim('No webhook endpoints registered.'));
    out();
    out(
      style.dim(
        '  Register one:\n' +
          '    await client.webhookSettings.createHmacEndpoint({ url: "https://…" })',
      ),
    );
    return EXIT.OK;
  }

  out(`${symbols.ok()} ${String(page.data.length)} webhook endpoint(s)`);
  out();

  for (const setting of page.data) {
    out(
      `  ${setting.isActive ? symbols.ok() : symbols.fail()} ${setting.id}  ${style.cyan(setting.url)}`,
    );
    out(
      style.dim(
        `      ${setting.driver} · ${authLabel(setting)} · ${scopeLabel(setting)} · ` +
          `${String(setting.maxRetries)} retries · sim ${setting.fireOnSimulated ? 'on' : 'OFF'}` +
          (setting.autoDisabledAt === null
            ? ''
            : ` · auto-disabled ${relativeTime(setting.autoDisabledAt)}`),
      ),
    );
  }

  return EXIT.OK;
}

function historyMarker(status: WebhookDeliveryStatus): string {
  if (status === 'DELIVERED') return symbols.ok();
  if (status === 'FAILED') return symbols.fail();
  if (status === 'RETRYING') return symbols.warn();
  return ' ';
}

function oneLine(body: string): string {
  return body.replace(/\s+/g, ' ').trim().slice(0, 80);
}

export async function webhookHistory(
  flags: WebhookAdminFlags,
): Promise<number> {
  const client = clientFromFlags(flags);
  const status = enumArg(flags.status, 'status', DELIVERY_STATUSES);
  const limit = intArg(flags.limit, 'limit');

  const page = await client.webhookHistories.list({
    ...(status === undefined ? {} : { status }),
    ...(limit === undefined ? {} : { limit }),
    ...(flags.setting === undefined ? {} : { settingId: flags.setting }),
  });

  if (flags.json === true) {
    printJson(page);
    return EXIT.OK;
  }

  if (page.data.length === 0) {
    out(style.dim('No delivery attempts match.'));
    return EXIT.OK;
  }

  out(`${symbols.ok()} ${String(page.data.length)} delivery attempt(s)`);
  out();

  for (const item of page.data) {
    out(
      `  ${historyMarker(item.status)} ${item.status.padEnd(10)} ` +
        `${String(item.responseStatus ?? '—').padStart(3)}  ` +
        `${String(item.attemptCount)} attempt${item.attemptCount === 1 ? ' ' : 's'}  ` +
        `${(item.durationMs === null ? '—' : `${String(item.durationMs)}ms`).padStart(7)}  ` +
        `${relativeTime(item.createdAt).padEnd(12)} ` +
        style.dim(item.setting?.url ?? item.settingId),
    );

    if (item.status !== 'DELIVERED' && item.responseBody) {
      out(style.dim(`      ${oneLine(item.responseBody)}`));
    }
    if (item.status === 'RETRYING' && item.nextAttemptAt !== null) {
      out(style.dim(`      next attempt ${relativeTime(item.nextAttemptAt)}`));
    }
    if (item.status === 'FAILED') {
      out(style.dim(`      gpmpay webhook retry ${item.id}`));
    }
  }

  // Exit 0 even with FAILED rows: listing is informational, and a non-zero
  // code would make this command unusable inside `set -e`.
  return EXIT.OK;
}

function retryHint(message: string): string | undefined {
  // The backend's 400s here are Vietnamese-only; this is an English CLI.
  if (message.includes('đã giao thành công')) {
    return 'This delivery already succeeded — there is nothing to retry.';
  }
  if (message.includes('đang tắt')) {
    return 'The webhook setting is disabled. Re-enable it, then retry.';
  }
  return undefined;
}

export async function webhookRetry(
  id: string | undefined,
  flags: WebhookAdminFlags,
): Promise<number> {
  const client = clientFromFlags(flags);
  const historyId = requireArg(id, 'history-id');

  let history: WebhookHistory;
  try {
    history = await client.webhookHistories.retry(historyId);
  } catch (error) {
    if (error instanceof GpmPayBadRequestError) {
      out(`${symbols.fail()} ${style.red(error.message)}`);
      const hint = retryHint(error.message);
      if (hint !== undefined) out(style.dim(`  ${hint}`));
      return EXIT.ERROR;
    }
    throw error;
  }

  if (flags.json === true) {
    printJson(history);
    return EXIT.OK;
  }

  out(`${symbols.ok()} Delivery ${style.bold(history.id)} re-queued`);
  out(
    fields([
      ['Status', history.status],
      ['Attempts', String(history.attemptCount)],
      ['Endpoint', history.setting?.url ?? history.settingId],
    ]),
  );
  return EXIT.OK;
}
